import { useTheme } from '@/hooks/useTheme';
import type { Theme } from './ThemeProvider';

/**
 * ThemeToggle - Button that cycles through light/dark/system theme modes
 *
 * Features:
 * - Cycles: light → dark → system → light
 * - Shows icon for current mode (sun/moon/monitor)
 * - Accessible with keyboard navigation
 * - Tooltip shows current mode
 *
 * @example
 * ```tsx
 * <ThemeToggle />
 * ```
 */
export function ThemeToggle() {
  const { theme, resolvedTheme, setTheme } = useTheme();

  const cycleTheme = () => {
    const nextTheme: Record<Theme, Theme> = {
      light: 'dark',
      dark: 'system',
      system: 'light',
    };
    setTheme(nextTheme[theme]);
  };

  const getThemeLabel = (): string => {
    const labels: Record<Theme, string> = {
      light: 'Light Mode',
      dark: 'Dark Mode',
      system: 'System Theme',
    };
    return labels[theme];
  };

  const getThemeIcon = () => {
    if (theme === 'system') {
      return (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          {/* Monitor/Display icon */}
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
          />
        </svg>
      );
    }

    if (resolvedTheme === 'dark') {
      return (
        <svg
          className="w-5 h-5"
          fill="none"
          stroke="currentColor"
          viewBox="0 0 24 24"
          aria-hidden="true"
        >
          {/* Moon icon */}
          <path
            strokeLinecap="round"
            strokeLinejoin="round"
            strokeWidth={2}
            d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z"
          />
        </svg>
      );
    }

    return (
      <svg
        className="w-5 h-5"
        fill="none"
        stroke="currentColor"
        viewBox="0 0 24 24"
        aria-hidden="true"
      >
        {/* Sun icon */}
        <path
          strokeLinecap="round"
          strokeLinejoin="round"
          strokeWidth={2}
          d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z"
        />
      </svg>
    );
  };

  return (
    <button
      onClick={cycleTheme}
      className="group relative inline-flex items-center justify-center rounded-lg p-2
                 text-gray-700 hover:bg-gray-100 hover:text-gray-900
                 dark:text-gray-300 dark:hover:bg-gray-800 dark:hover:text-gray-100
                 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2
                 dark:focus:ring-offset-gray-900
                 transition-colors duration-200"
      aria-label={`Switch theme (current: ${getThemeLabel()})`}
      title={getThemeLabel()}
      type="button"
    >
      {getThemeIcon()}

      {/* Tooltip */}
      <span
        className="absolute -bottom-10 left-1/2 -translate-x-1/2 px-2 py-1
                   bg-gray-900 dark:bg-gray-700 text-white text-xs rounded
                   opacity-0 group-hover:opacity-100 pointer-events-none
                   transition-opacity duration-200 whitespace-nowrap z-50"
      >
        {getThemeLabel()}
      </span>
    </button>
  );
}

/**
 * ThemeToggleWithLabel - ThemeToggle variant with visible text label
 * Useful for settings pages or when more explicit UI is desired
 *
 * @example
 * ```tsx
 * <ThemeToggleWithLabel />
 * ```
 */
export function ThemeToggleWithLabel() {
  const { theme, setTheme } = useTheme();

  return (
    <div className="flex items-center gap-2">
      <label className="text-sm font-medium text-gray-700 dark:text-gray-300">
        Theme
      </label>
      <select
        value={theme}
        onChange={(e) => setTheme(e.target.value as Theme)}
        className="rounded-lg border border-gray-300 dark:border-gray-600
                   bg-white dark:bg-gray-800
                   text-gray-900 dark:text-gray-100
                   px-3 py-2 text-sm
                   focus:outline-none focus:ring-2 focus:ring-blue-500
                   transition-colors duration-200"
      >
        <option value="light">Light</option>
        <option value="dark">Dark</option>
        <option value="system">System</option>
      </select>
    </div>
  );
}
